import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { message } from 'antd';
import { apiService } from '../../services/api';
import { useBackdropClose } from '../../hooks/useBackdropClose';
import './connection-create-panel.css';

/**
 * ConnectionCreatePanel — slide-in панель создания/редактирования подключения
 * По макету Figma. Overlay + правая панель 480 px.
 *
 * Заменяет Ant Design Modal для создания и редактирования.
 */

// ==================== SVG ICONS ====================

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const InfoIcon = ({ title }) => (
  <svg
    className="ccp__info-icon"
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-label={title}
  >
    <title>{title}</title>
    <circle cx="6" cy="6" r="5.25" stroke="currentColor" strokeWidth="1" />
    <path d="M6 5.5V8.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
    <circle cx="6" cy="3.75" r="0.6" fill="currentColor" />
  </svg>
);

const ChevronUpIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M3.5 7.5L6 5L8.5 7.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M3.5 4.5L6 7L8.5 4.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const RemoveIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ==================== DEFAULTS ====================

const DEFAULTS = {
  name: '',
  server_url: '',
  username: '',
  password: '',
  city: '',
  alias: '',
  use_https: false,
  reserve_modems_count: 3,
  slimiter_unit: 'kbytes',
  ssh_host: '',
  ssh_port: 22,
  ssh_username: 'root',
  ssh_password: '',
  ssh_private_key_encrypted: '',
  ssh_public_key: '',
  auto_allocate_ports: true,
  port_allocation_strategy: 'sequential',
  port_ranges: [],
};

// ==================== SUB-COMPONENTS ====================

const TextField = ({ label, required, value, onChange, onKeyDown, placeholder, type = 'text', error, autoComplete }) => (
  <div className="ccp__field">
    <div className="ccp__label">
      {required && <span className="ccp__label-required">*</span>}
      <span className="ccp__label-text">{label}</span>
    </div>
    <input
      type={type}
      className={`ccp__input${error ? ' ccp__input--error' : ''}`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      aria-label={label}
      autoComplete={autoComplete}
    />
    {error && <span className="ccp__field-error">{error}</span>}
  </div>
);

const toNumberInputText = (value) => (
  value === '' || value === undefined || value === null ? '' : String(value)
);

const parseFiniteInt = (value) => {
  if (value === '' || value === undefined || value === null) return null;
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  return Math.trunc(num);
};

/** 0 — валидное значение; пустое/мусор → fallback (не через `||`, иначе 0 схлопнется). */
const coerceReserveModemsCount = (value, fallback = 3) => {
  const num = parseFiniteInt(value);
  if (num === null) return fallback;
  return Math.min(100, Math.max(0, num));
};

const NumberField = ({ label, value, onChange, onKeyDown, min, max, step = 1, infoTitle, placeholder }) => {
  // Локальный черновик: type=number + controlled number ломает ввод (промежуточные '' / набор цифр).
  const [text, setText] = useState(() => toNumberInputText(value));

  useEffect(() => {
    setText(toNumberInputText(value));
  }, [value]);

  const commitNumber = (num) => {
    let next = num;
    if (min !== undefined && next < min) next = min;
    if (max !== undefined && next > max) next = max;
    onChange(next);
    setText(String(next));
  };

  const handleIncrement = () => {
    const num = parseFiniteInt(text) ?? (min ?? 0);
    commitNumber(Math.min(num + step, max ?? Infinity));
  };

  const handleDecrement = () => {
    const num = parseFiniteInt(text) ?? (min ?? 0);
    commitNumber(Math.max(num - step, min ?? -Infinity));
  };

  const handleBlur = () => {
    if (text === '') {
      onChange('');
      return;
    }
    const num = parseFiniteInt(text);
    if (num === null) {
      setText(toNumberInputText(value));
      return;
    }
    commitNumber(num);
  };

  return (
    <div className="ccp__field">
      <div className="ccp__label">
        <span className="ccp__label-text">{label}</span>
        {infoTitle && <InfoIcon title={infoTitle} />}
      </div>
      <div className="ccp__number-wrap">
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          className="ccp__input ccp__input--number"
          value={text}
          onChange={(e) => {
            const raw = e.target.value;
            // Только цифры (и пустая строка) — без type=number quirks браузера
            if (raw !== '' && !/^\d+$/.test(raw)) return;
            setText(raw);
            if (raw === '') {
              onChange('');
              return;
            }
            onChange(Number(raw));
          }}
          onBlur={handleBlur}
          onKeyDown={onKeyDown}
          min={min}
          max={max}
          step={step}
          placeholder={placeholder}
          aria-label={label}
        />
        <div className="ccp__number-arrows">
          <button
            type="button"
            className="ccp__number-arrow"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleIncrement}
            tabIndex={-1}
            aria-label="Увеличить"
          >
            <ChevronUpIcon />
          </button>
          <button
            type="button"
            className="ccp__number-arrow"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleDecrement}
            tabIndex={-1}
            aria-label="Уменьшить"
          >
            <ChevronDownIcon />
          </button>
        </div>
      </div>
    </div>
  );
};

const ToggleRow = ({ label, checked, onChange, infoTitle }) => (
  <div className="ccp__toggle-row">
    <span className="ccp__toggle-label">{label}</span>
    <button
      type="button"
      className={`ccp__toggle${checked ? ' ccp__toggle--on' : ''}`}
      onClick={() => onChange(!checked)}
      role="switch"
      aria-checked={checked}
      aria-label={label}
      tabIndex={0}
    >
      <span className="ccp__toggle-dot" />
    </button>
    {infoTitle && <InfoIcon title={infoTitle} />}
  </div>
);

// ==================== MAIN COMPONENT ====================

const ConnectionCreatePanel = ({
  visible = false,
  onClose = null,
  onSubmit = null,
  submitting = false,
  editingConnection = null,
}) => {
  const [formData, setFormData] = useState({ ...DEFAULTS });
  const [errors, setErrors] = useState({});
  const [generatingSshKey, setGeneratingSshKey] = useState(false);
  const [sshKeyStatus, setSshKeyStatus] = useState(null);

  // ==================== RESET / PRE-FILL ON OPEN ====================
  // Зависимость только visible + id: иначе новый object ref editingConnection
  // (рефетч таблицы) сбрасывал форму во время ввода — поле «залипало» на старом значении.
  const editingConnectionId = editingConnection?.id ?? null;
  useEffect(() => {
    if (!visible) return;

    if (editingConnection) {
      const reserveRaw = editingConnection.reserve_modems_count;
      setFormData({
        name: editingConnection.name || '',
        server_url: editingConnection.server_url || '',
        username: editingConnection.username || '',
        password: '',
        city: editingConnection.city || '',
        alias: editingConnection.alias || '',
        use_https: editingConnection.use_https || false,
        reserve_modems_count: reserveRaw === undefined || reserveRaw === null
          ? 3
          : coerceReserveModemsCount(reserveRaw, 3),
        slimiter_unit: editingConnection.slimiter_unit || 'kbytes',
        ssh_host: editingConnection.ssh_host || '',
        ssh_port: editingConnection.ssh_port || 22,
        ssh_username: editingConnection.ssh_username || 'root',
        ssh_password: '',
        ssh_private_key_encrypted: editingConnection.ssh_private_key_encrypted || '',
        ssh_public_key: editingConnection.ssh_public_key || '',
        auto_allocate_ports: editingConnection.auto_allocate_ports !== undefined
          ? editingConnection.auto_allocate_ports : true,
        port_allocation_strategy: editingConnection.port_allocation_strategy || 'sequential',
        port_ranges: Array.isArray(editingConnection.port_ranges)
          ? editingConnection.port_ranges.map((r) => ({ ...r }))
          : [],
      });

      const hasSshKey = editingConnection.has_ssh_key === true || (
        editingConnection.ssh_private_key_encrypted &&
        editingConnection.ssh_public_key &&
        String(editingConnection.ssh_private_key_encrypted).trim() !== '' &&
        String(editingConnection.ssh_public_key).trim() !== ''
      );
      setSshKeyStatus(hasSshKey ? 'success' : null);
    } else {
      setFormData({ ...DEFAULTS, port_ranges: [] });
      setSshKeyStatus(null);
    }
    setErrors({});
    // editingConnection читаем только при смене visible / id
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, editingConnectionId]);

  // ==================== HANDLERS ====================
  const handleChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  }, [errors]);

  const validate = useCallback(() => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Введите название подключения';
    }
    if (!formData.server_url.trim()) {
      newErrors.server_url = 'Введите URL сервера';
    } else if (!/^[^:]+:\d+$/.test(formData.server_url.trim())) {
      newErrors.server_url = 'Формат: IP:PORT';
    }
    if (!editingConnection && !formData.username.trim()) {
      newErrors.username = 'Введите имя пользователя';
    }
    if (!editingConnection && !formData.password.trim()) {
      newErrors.password = 'Введите пароль';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, editingConnection]);

  const handleSubmit = useCallback(() => {
    if (!validate()) return;
    if (!onSubmit) return;

    const values = {
      name: formData.name.trim(),
      server_url: formData.server_url.trim(),
      ...(formData.username.trim() ? { username: formData.username.trim() } : {}),
      ...(formData.password ? { password: formData.password } : {}),
      city: formData.city.trim(),
      alias: formData.alias.trim(),
      use_https: formData.use_https,
      reserve_modems_count: coerceReserveModemsCount(formData.reserve_modems_count, 3),
      slimiter_unit: formData.slimiter_unit === 'bits' ? 'bits' : 'kbytes',
      ssh_host: formData.ssh_host.trim(),
      ssh_port: Number(formData.ssh_port) || 22,
      ssh_username: formData.ssh_username.trim() || 'root',
      ssh_password: formData.ssh_password,
      auto_allocate_ports: formData.auto_allocate_ports,
      port_allocation_strategy: formData.port_allocation_strategy,
      port_ranges: formData.port_ranges,
    };
    // GET больше не отдаёт ciphertext — пустое поле не шлём, чтобы не затереть ключ в БД
    if (formData.ssh_private_key_encrypted && String(formData.ssh_private_key_encrypted).trim()) {
      values.ssh_private_key_encrypted = formData.ssh_private_key_encrypted;
    }
    if (formData.ssh_public_key && String(formData.ssh_public_key).trim()) {
      values.ssh_public_key = formData.ssh_public_key;
    }

    onSubmit(values);
  }, [formData, validate, onSubmit]);

  const handleClose = useCallback(() => {
    if (!submitting && onClose) onClose();
  }, [submitting, onClose]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  }, [handleSubmit]);

  // ==================== SSH KEY GENERATION ====================
  const handleGenerateSshKey = useCallback(async () => {
    if (!formData.ssh_host.trim()) {
      message.error('Сначала укажите SSH Host');
      return;
    }

    try {
      setGeneratingSshKey(true);
      setSshKeyStatus(null);

      const response = await apiService.generateAndInstallSshKey({
        connection_id: editingConnection?.id,
        ssh_host: formData.ssh_host.trim(),
        ssh_port: Number(formData.ssh_port) || 22,
        ssh_username: formData.ssh_username.trim() || 'root',
        ssh_password: formData.ssh_password || undefined,
      });

      if (response.data.success) {
        setFormData(prev => ({
          ...prev,
          ssh_private_key_encrypted: response.data.ssh_private_key_encrypted,
          ssh_public_key: response.data.ssh_public_key,
        }));
        setSshKeyStatus('success');
        message.success('SSH ключ успешно сгенерирован и установлен на сервер');
      } else {
        throw new Error(response.data.message || 'Ошибка генерации SSH ключа');
      }
    } catch (error) {
      message.error(`Ошибка генерации SSH ключа: ${error.response?.data?.detail || error.message}`);
      setSshKeyStatus('error');
    } finally {
      setGeneratingSshKey(false);
    }
  }, [formData.ssh_host, formData.ssh_port, formData.ssh_username, formData.ssh_password, editingConnection]);

  // ==================== PORT RANGES ====================
  const handleAddPortRange = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      port_ranges: [...prev.port_ranges, { start: '', end: '' }],
    }));
  }, []);

  const handleRemovePortRange = useCallback((index) => {
    setFormData(prev => ({
      ...prev,
      port_ranges: prev.port_ranges.filter((_, i) => i !== index),
    }));
  }, []);

  const handlePortRangeChange = useCallback((index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.port_ranges];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, port_ranges: updated };
    });
  }, []);

  // Затемнение закрывает форму только настоящим щелчком — нажатие и отпускание на нём (просьба владельца 28.10)
  const backdrop = useBackdropClose(handleClose);

  // ==================== EARLY RETURN ====================
  if (!visible) return null;

  const isEditMode = !!editingConnection;

  return createPortal(
    <div
      className="ccp-overlay"
      {...backdrop}
      role="dialog"
      aria-modal="true"
      aria-label={isEditMode ? 'Редактирование подключения' : 'Создание подключения'}
    >
      <div className="ccp">
        {/* ===== HEADER ===== */}
        <div className="ccp__header">
          <h2 className="ccp__title">
            {isEditMode ? 'редактирование подключения' : 'создание подключения'}
          </h2>
          <button
            type="button"
            className="ccp__close-btn"
            onClick={handleClose}
            disabled={submitting}
            aria-label="Закрыть"
            tabIndex={0}
          >
            <CloseIcon />
          </button>
        </div>

        {/* ===== BODY ===== */}
        <div className="ccp__body">

          {/* --- Section: Основные настройки --- */}
          <div className="ccp__section">
            <span className="ccp__section-title">Основные настройки</span>

            <div className="ccp__row">
              <TextField
                label="Название подключения"
                required
                value={formData.name}
                onChange={(v) => handleChange('name', v)}
                onKeyDown={handleKeyDown}
                placeholder="Введите название"
                error={errors.name}
              />
              <TextField
                label="URL сервера"
                required
                value={formData.server_url}
                onChange={(v) => handleChange('server_url', v)}
                onKeyDown={handleKeyDown}
                placeholder="212.12.12.12:800"
                error={errors.server_url}
              />
            </div>

            <div className="ccp__row">
              <TextField
                label="Имя пользователя"
                required={!isEditMode}
                value={formData.username}
                onChange={(v) => handleChange('username', v)}
                onKeyDown={handleKeyDown}
                placeholder={isEditMode ? 'Оставьте пустым, если не хотите изменять' : 'Введите имя пользователя'}
                autoComplete="username"
                error={errors.username}
              />
              <TextField
                label="Пароль"
                required={!isEditMode}
                value={formData.password}
                onChange={(v) => handleChange('password', v)}
                onKeyDown={handleKeyDown}
                placeholder={isEditMode ? 'Оставьте пустым, если не хотите изменять' : 'Введите пароль'}
                type="password"
                autoComplete="new-password"
                error={errors.password}
              />
            </div>

            <div className="ccp__row">
              <TextField
                label="Город"
                value={formData.city}
                onChange={(v) => handleChange('city', v)}
                onKeyDown={handleKeyDown}
                placeholder="Введите город"
              />
              <TextField
                label="Псевдоним"
                value={formData.alias}
                onChange={(v) => handleChange('alias', v)}
                onKeyDown={handleKeyDown}
                placeholder="Введите псевдоним"
              />
            </div>

            <ToggleRow
              label="Использовать HTTPS"
              checked={formData.use_https}
              onChange={(v) => handleChange('use_https', v)}
            />

            <NumberField
              label="Количество резервных модемов"
              value={formData.reserve_modems_count}
              onChange={(v) => handleChange('reserve_modems_count', v)}
              onKeyDown={handleKeyDown}
              min={0}
              max={100}
              infoTitle="Количество модемов, которые должны оставаться свободными (без прокси с лимитерами)"
              placeholder="3"
            />

            <div className="ccp__segment-row">
              <div className="ccp__label">
                <span className="ccp__label-text">Единицы slimiter</span>
                <InfoIcon title="кбайт — Kraken принимает/отдаёт КБайт/с. бит — только бит/с (×8000). Смена тумблера перепишет все прокси этого подключения из БД менеджера." />
              </div>
              <div className="ccp__unit-seg" role="radiogroup" aria-label="Единицы slimiter">
                <button
                  type="button"
                  role="radio"
                  aria-checked={formData.slimiter_unit !== 'bits'}
                  className={`ccp__unit-seg-btn${formData.slimiter_unit !== 'bits' ? ' ccp__unit-seg-btn--on' : ''}`}
                  onClick={() => handleChange('slimiter_unit', 'kbytes')}
                  tabIndex={0}
                >
                  кбайт
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={formData.slimiter_unit === 'bits'}
                  className={`ccp__unit-seg-btn${formData.slimiter_unit === 'bits' ? ' ccp__unit-seg-btn--on' : ''}`}
                  onClick={() => handleChange('slimiter_unit', 'bits')}
                  tabIndex={0}
                >
                  бит
                </button>
              </div>
              {editingConnection && formData.slimiter_unit !== (editingConnection.slimiter_unit || 'kbytes') ? (
                <p className="ccp__unit-rewrite-note" role="status">
                  После сохранения все прокси этого подключения будут переписаны в Kraken под новую единицу.
                </p>
              ) : null}
            </div>
          </div>

          {/* --- Section: SSH настройки --- */}
          <div className="ccp__section">
            <span className="ccp__section-title">SSH настройки</span>

            <div className="ccp__row">
              <TextField
                label="SSH хост"
                value={formData.ssh_host}
                onChange={(v) => handleChange('ssh_host', v)}
                onKeyDown={handleKeyDown}
                placeholder="192.168.0.1"
                autoComplete="off"
              />
              <NumberField
                label="Порт"
                value={formData.ssh_port}
                onChange={(v) => handleChange('ssh_port', v)}
                onKeyDown={handleKeyDown}
                min={1}
                max={65535}
                infoTitle="SSH порт (по умолчанию 22)"
                placeholder="22"
              />
            </div>

            <div className="ccp__row">
              <TextField
                label="Имя пользователя"
                value={formData.ssh_username}
                onChange={(v) => handleChange('ssh_username', v)}
                onKeyDown={handleKeyDown}
                placeholder="Введите имя пользователя"
                autoComplete="off"
              />
              <TextField
                label="Пароль"
                value={formData.ssh_password}
                onChange={(v) => handleChange('ssh_password', v)}
                onKeyDown={handleKeyDown}
                placeholder="Введите пароль"
                type="password"
                autoComplete="new-password"
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center' }}>
              <button
                type="button"
                className="ccp__action-btn"
                onClick={handleGenerateSshKey}
                disabled={generatingSshKey}
                aria-label="Сгенерировать SSH ключ"
                tabIndex={0}
              >
                {generatingSshKey ? 'Генерация...' : 'Сгенерировать SSH ключ'}
              </button>
            </div>

            {sshKeyStatus && (
              <span className={`ccp__ssh-status ccp__ssh-status--${sshKeyStatus}`}>
                {sshKeyStatus === 'success' ? 'Ключ установлен' : 'Ошибка генерации'}
              </span>
            )}
          </div>

          {/* --- Section: Настройки портов --- */}
          <div className="ccp__section ccp__section--grow">
            <span className="ccp__section-title">Настройки портов</span>

            <ToggleRow
              label="Автоматическое выделение портов"
              checked={formData.auto_allocate_ports}
              onChange={(v) => handleChange('auto_allocate_ports', v)}
              infoTitle="Автоматически создавать записи PortAllocation при настройке диапазонов"
            />

            <ToggleRow
              label="Последовательное выделение портов"
              checked={formData.port_allocation_strategy === 'sequential'}
              onChange={(v) => handleChange('port_allocation_strategy', v ? 'sequential' : 'random')}
              infoTitle="Последовательно или случайно выделять порты из диапазона"
            />

            {formData.port_ranges.map((range, index) => (
              <div className="ccp__port-range" key={index}>
                <NumberField
                  label={`Начальный порт`}
                  value={range.start}
                  onChange={(v) => handlePortRangeChange(index, 'start', v)}
                  onKeyDown={handleKeyDown}
                  min={1000}
                  max={65535}
                  placeholder="1000"
                />
                <NumberField
                  label={`Конечный порт`}
                  value={range.end}
                  onChange={(v) => handlePortRangeChange(index, 'end', v)}
                  onKeyDown={handleKeyDown}
                  min={1000}
                  max={65535}
                  placeholder="10200"
                />
                <button
                  type="button"
                  className="ccp__port-range-remove"
                  onClick={() => handleRemovePortRange(index)}
                  aria-label={`Удалить диапазон ${index + 1}`}
                  tabIndex={0}
                >
                  <RemoveIcon />
                </button>
              </div>
            ))}

            <button
              type="button"
              className="ccp__action-btn"
              onClick={handleAddPortRange}
              aria-label="Добавить диапазон портов"
              tabIndex={0}
            >
              Добавить диапазон портов
            </button>
          </div>
        </div>

        {/* ===== FOOTER ===== */}
        <div className="ccp__footer">
          <button
            type="button"
            className="ccp__btn ccp__btn--cancel"
            onClick={handleClose}
            disabled={submitting}
            tabIndex={0}
          >
            Отмена
          </button>
          <button
            type="button"
            className="ccp__btn ccp__btn--submit"
            onClick={handleSubmit}
            disabled={submitting}
            tabIndex={0}
          >
            {submitting
              ? (isEditMode ? 'Сохранение...' : 'Создание...')
              : (isEditMode ? 'Сохранить' : 'Создать')
            }
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ConnectionCreatePanel;
